import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, of, ReplaySubject, switchMap } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { ProjectMedia } from '../models/project-media.model';
import { Project } from '../models/project.model';
import { CommunicationManagerService } from './communication-manager.service';
import { LocalStorageService } from './storage.service';
import * as _ from 'lodash-es';
import { catchError, tap } from 'rxjs/operators';
import { SessionManagerService } from './session-manager.service';
import { CompanyIncomeMontlyItem } from '../models/company-income-monthly-item.model';
import { VersionInfo } from '@ops/shared-types';


@Injectable({
  providedIn: 'root',
})
export class ProjectManagerService {
  //TODO: new projectIdHeader model, create new and pass id in constructor when calling CM methods
  private currentProject: Project | undefined;
  private lastEmittedProjectJson: string | undefined;

  projectsLite: Array<Project> | undefined;
  project$: ReplaySubject<Project> = new ReplaySubject<Project>();
  currentProjectUpdatedSig = signal<Project | undefined>(undefined);
  currentProjectUpdated$: Observable<Project | undefined> = toObservable(this.currentProjectUpdatedSig);
  currentProjectSig = signal<Project | undefined>(undefined);
  currentProject$: Observable<Project | undefined> = toObservable(this.currentProjectSig);

  projectSubscriptionMissing$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  constructor(
    private cm: CommunicationManagerService,
    private localStorageService: LocalStorageService,
    private sessionManager: SessionManagerService
  ) {
    const storedProject = this.localStorageService.getCurrentProject();
    if (storedProject) {
      this.currentProject = storedProject;
      try { 
        this.lastEmittedProjectJson = JSON.stringify(storedProject);
        // console.log('storedProject', storedProject);
        this.project$.next(storedProject);
      } catch {}
    }
  }

  setCurrentProject(project: Project) {
    this.currentProject = project;
    this.currentProjectSig.set(project);
    this.localStorageService.setCurrentProject(project as Project);
    // Do not trigger the signal here; legacy observable still emits
    try { this.lastEmittedProjectJson = JSON.stringify(project); } catch {}
    // console.log('setCurrentProject', project);
    this.project$.next(project);
    this.currentProjectUpdatedSig.set(project);
    // Notify project status guard service about the current project
    // This will trigger prevention logic if project status >= 2
    this.notifyProjectStatusGuard(project);
  }

  updateCurrentProject(mutator: (project: Project) => Project) {
    // console.log('updateCurrentProject');
    const current = this.currentProject ?? this.localStorageService.getCurrentProject();
    if (!current) return;
    const updated = mutator({ ...current });
    // Persist
    this.localStorageService.setCurrentProject(updated);
    this.localStorageService.updateProjectCache(updated);
    // Signal here
    this.currentProject = updated;
    this.currentProjectUpdatedSig.set(updated);
    try { this.lastEmittedProjectJson = JSON.stringify(updated); } catch {}
    // console.log('updated', updated);
    // this.project$.next(updated);
  }

  /**
   * Notify the project status guard service about the current project
   * This method will be called by the app component to integrate with the guard service
   */
  notifyProjectStatusGuard(project: Project) {
    // This will be called by the app component to set the current project in the guard service
    // The actual integration happens in the app component
  }

  getProjectSubscriptionMissingObservable() {
    return this.projectSubscriptionMissing$;
  }

  //gets project cost _id or creates a new one if cost doesn't exist and returns the _id
  getOrCreateProjectCost(projectId: string, companyId: string) {
    return this.cm.post(`project/module`, {}, [
      { headerName: 'projectId', headerValue: projectId },
      { headerName: 'companyId', headerValue: companyId },
    ]);
  }

  getNewProject(project: Project, companyId: string) {
    const projectDto = this.cleanProjectObject(project);
    delete projectDto['company'];
    // console.log(projectDto);
    return this.cm
      .post('project/create', projectDto, [
        { headerName: 'companyId', headerValue: companyId },
      ])
      .pipe(
        switchMap((id) => {
          // this.localStorageService.clearEssential();
          this.getAllProjects(this.sessionManager.getCurrentCompany() ?? '');
          return of(id);
        })
      );
  }

  private cleanProjectObject(project: Project): Project {
    const clone: any = _.cloneDeep(project);

    Object.keys(clone).forEach((field) => {
      if (!field || clone[field] === '') delete clone[field];
    });
    return clone;
  }

  getCurrentPojectObservable(): Observable<Project> {
    return this.project$;
  }

  getCurrentProject() {
    const cp = this.localStorageService.getCurrentProject();
    if (cp && !this.currentProject) {
      this.setCurrentProject(cp);
      return cp;
    }
    return this.currentProject;
  }

  getAllProjects(companyId: string): Observable<Project[]> {
    // if(!companyId) {
    //   return of([]);
    // }
    // TODO refresh every 5 min.
    // TODO as a cover we will add phisical refresh button to trigger pulling of all projects and setting them in the localStorage.
    // current project stays the same.
    // console.log('SHOULD TRACK 2');

    // console.log("SESSSSS\n\n", this.sessionManager.getCurrentCompany(), companyId);

    // if(!companyId || companyId !== this.localStorageService.getCurrentCompany()?._id) {
    //   this.localStorageService.setAllProjects([]);
    // }

    // if(!companyId) {
    //   return of([]);
    //   // companyId = this.localStorageService.getCurrentCompany()?._id;
    // }

    const localProjects = this.localStorageService.getAllProjects();
    if (localProjects.length > 0) {
      return of(localProjects);
    } else {
      return this.cm
        .get('project/all', [
          { headerName: 'companyId', headerValue: companyId },
        ])
        .pipe(
          tap((projects) => {
            this.localStorageService.setAllProjects(projects);
            this.projectSubscriptionMissing$.next(false);
            // this.loadingService.setLoading(false);
          }),
          catchError((error) => {
            // console.log('error', error);
            this.localStorageService.setAllProjects([]);
            this.projectSubscriptionMissing$.next(true);
            return of([]); // Return empty array as observable
          }),
          switchMap((projects) => {
            // console.log('SHOULD TRACK 4');

            return of(projects);
          })
        );
    }
  }

  getAllProjectsLite() {
    return this.cm.get('project/all/lite');
  }

  getProjectById(id: string): Observable<Project> {
    const localProject = this.localStorageService.getProjectById(id);
    if (localProject && Object.keys(localProject).length > 0) {
      return of(localProject);
    } else {
      return this.cm
        .get(`project`, [{ headerName: 'projectId', headerValue: id }])
        .pipe(
          switchMap((project) => {
            this.localStorageService.setProjectById(id, project);
            return of(project);
          })
        );
    }
  }

  getProjectVersioningInfo(id: string) {
    return this.cm.get('project/versioning', [
      { headerName: 'projectId', headerValue: id },
    ]);
  }

  updateProject(projectId: string, fieldsToUpdate: any) {
    return this.cm
      .put(`project/update`, fieldsToUpdate, [
        { headerName: 'projectId', headerValue: projectId },
      ])
      .pipe(
        switchMap((project) => {
          if (Object.keys(fieldsToUpdate).includes('estimateSentBy')) {
            return of(project);
          }
          const projectInFocus = this.localStorageService.getProjectById(
            projectId
          ) as any;

          if (!projectInFocus) {
            return of(project);
          }

          const keyValues = Object.keys(fieldsToUpdate) || [];

          for (let i = 0, l = keyValues.length; i < l; i++) {
            delete projectInFocus[keyValues[i]];
          }

          const updatedProject = _.merge(projectInFocus, fieldsToUpdate) as Project;
          this.localStorageService.updateProjectCache(updatedProject);

          if (
            projectId === this.currentProject?._id &&
            (fieldsToUpdate['versionName'] === null ||
            fieldsToUpdate['versionName'] === undefined)
          ) {
            this.updateCurrentProject(project => ({...project, ...updatedProject}));
          }

          return of(project);
        })
      );
  }

  updateProjectinfo(
    versionLink: string,
    fieldsToUpdate: any,
    fieldsCover: any,
    projectId: string
  ) {
    return this.cm
      .put(`project/info/${versionLink}`, fieldsToUpdate, [
        { headerName: 'projectId', headerValue: projectId },
      ])
      .pipe(
        switchMap((projects) => {
          const projectsInFocus =
            this.localStorageService.getAllProjectsByVersionLink(
              versionLink
            ) as any;

          const keyValues = Object.keys(fieldsCover) || [];

          let updatedProject: Project;

          projectsInFocus.forEach((p: any) => {
            for (let i = 0, l = keyValues.length; i < l; i++) {
              delete p[keyValues[i]];
            }

            updatedProject = _.merge(p, {
              ...fieldsCover,
            });
            this.localStorageService.setProjectById(
              p._id,
              updatedProject as Project
            );
          });
          return of(projects);
        })
      );
  }

  updateDisplayOptionsForAllVersions(projectId: string, versionLink: string, displayOptionsToUpdate: Array<VersionInfo>){
    const payload = {
      displayOptionsJson: JSON.stringify({ displayedVersions: displayOptionsToUpdate?.map(v => v._id) || [] })
    }

    return this.cm.put(`project/options/${versionLink}`, payload, [{ headerName: 'projectId', headerValue: projectId }])
    .pipe(
      tap((response) => {
        const projectsInFocus =
            this.localStorageService.getAllProjectsByVersionLink(
              versionLink
            ) as any;
        
        projectsInFocus.forEach((p: any) => {
          const parsedOptions = p.displayOptionsJson ? JSON.parse(p.displayOptionsJson) : {};
          parsedOptions.displayedVersions = displayOptionsToUpdate?.map(v => v._id) || [];
          p.displayOptionsJson = JSON.stringify(parsedOptions);
          this.localStorageService.setProjectById(p._id, p);
        });

        return of(response);
      })
    );
  }

  deleteProject(projectId: string) {
    return this.cm
      .delete('project', [{ headerName: 'projectId', headerValue: projectId }])
      .pipe(
        switchMap((response) => {
          this.localStorageService.deleteProject(projectId);
          return of(response);
        })
      );
  }

  archiveProject(projectId: string) {
    return this.cm
      .putArchive('project/archive', [
        { headerName: 'projectId', headerValue: projectId },
      ])
      .pipe(
        switchMap((response) => {
          this.localStorageService.deleteProject(projectId);
          return of(response);
        })
      );
  }

  //TODO: get rid of `any` type here, move VersionGenesis to models to avoid circular refference
  //TODO: see if we can move the header definition further down towards the
  newVersionClone(payload: any, projectId: string, companyId: string) {
    return this.cm.post('project/clone', payload, [
      { headerName: 'projectId', headerValue: projectId },
      { headerName: 'companyId', headerValue: companyId },
    ]);
  }

  addMediaToProject(media: ProjectMedia) {
    if (!this.currentProject) {
      return;
    }

    if (!this.currentProject?.projectMedia)
      this.currentProject.projectMedia = [];

    this.currentProject.projectMedia.push(media);
  }

  getAllProjectsForCompanyLoadedWithCostsForMonth(
    companyId: string,
    month: number,
    year: number
  ): Observable<Array<Project>> {
    return this.cm.postAndGetJson(`project/all/loaded/costItem`, {
      companyId: companyId,
      year: year,
      month: month,
    });
  }

  getProjectLoaded(
    projectId: string, 
    costItems?: boolean,
    itemGroups?: boolean,
    medias?: boolean, 
    invoices?: boolean, 
    costItemComments?: boolean, 
    itemGroupComments?: boolean): Observable<Project> {
      return this.cm.postAndGetJson(`project/loaded`, {
        itemGroups: itemGroups ?? false,
        costItems: costItems ?? false,
        medias: medias ?? false,
        invoices: invoices ?? false,
        costItemComments: costItemComments ?? false,
        itemGroupComments: itemGroupComments ?? false,
      }, 
      [{ headerName: 'projectId', headerValue: projectId }]);
  }

  // TODO WRONG SCOPE
  // Move this to new service or company service
  private calculateMonthlyPayments(
    items: Array<CompanyIncomeMontlyItem>,
    year: number,
    month: number
  ) {
    return items.map((item) => {
      // Filter payments for the specified year/month
      const monthlyPayments = item?.payments?.filter((payment) => {
        if (!payment?.date) return false;

        const paymentDate = new Date(payment?.date);
        return (
          paymentDate.getFullYear() === year &&
          paymentDate.getMonth() + 1 === month
        );
      });

      // Calculate the sum of payments
      const monthlySum = monthlyPayments?.reduce(
        (sum, payment) => sum + (payment?.amount || 0),
        0
      );

      const paymentDaysInMonth: number[] = [];
      monthlyPayments?.forEach((paymentItem) => {
        if (!paymentItem?.date) return;

        const paymentDate = new Date(paymentItem?.date);
        paymentDaysInMonth.push(paymentDate.getDate());
      });

      paymentDaysInMonth.sort((a: number, b: number) => b - a);
      const lastPaymentDateInMOnth = new Date(
        year,
        month - 1,
        paymentDaysInMonth?.[0] || 1
      );

      const expectedThisMonthSum =
        new Date(item.paymentExpectedDate).getMonth() + 1 === month;
      // Create a new object with the additional field
      return {
        ...item,
        lastPaymentDateInMonth: paymentDaysInMonth?.[0]
          ? lastPaymentDateInMOnth
          : null,
        monthlyPaymentSum: monthlySum,
        expectedThisMonthSum: expectedThisMonthSum
          ? item.invoiceAmount - item.receivedAmount
          : 0,
        monthlyPaymentCount: monthlyPayments?.length,
      };
    });
  }

  // TODO WRONG SCOPE
  // Move this to new service or company service
  getAllIncomesForCompanyForMonth(
    companyId: string,
    month: number,
    year: number
  ) {
    const incomeForMonth = `${year}-${month}-01`;
    return this.cm
      .get(`company/income/monthly/${incomeForMonth}`, [
        {
          headerName: 'companyId',
          headerValue: companyId,
        },
      ])
      .pipe(
        switchMap((incomeItems: Array<CompanyIncomeMontlyItem>) => {
          const calculatedIncomeItems = this.calculateMonthlyPayments(
            incomeItems,
            year,
            month
          );
          return of(calculatedIncomeItems);
        })
      );
  }

  addEstimateToApproved(projectId: string) {
    return this.cm.put(`project/approveExtra`, {}, [
      { headerName: 'projectId', headerValue: projectId },
    ]);
  }

  getProjectProfitability(projectId: string) {
    return this.cm.get(`project/profitability`, [
      {
        headerName: 'projectId',
        headerValue: projectId,
      },
    ]);
  }

  duplicateProject(
    project: Project,
    projectCode: string, 
    deliverables: boolean, 
    departments: boolean, 
    productionCost: boolean) :Observable<any> {
    const payload = {
      projectCode: projectCode,
      deliverables: deliverables,
      departments: departments,
      productionCost: productionCost,
    };

    return (this.cm.post(`project/duplicate/${project.versionLink}`, 
      payload, [
      { headerName: 'projectId', headerValue: project._id },
      { headerName: 'companyId', headerValue: project.company ?? '' },
    ]) as unknown as Observable<Array<string>>)
  }
}
